'use client';

import { getSportIconMeta, type WeeklySummaryType } from '@trainme/api/client';
import type {
  ActivityWithRelations,
  WorkoutWithRelations,
} from '@/utils/types';

const ICON_TO_CANONICAL: Record<string, string> = {
  run: 'Run',
  ride: 'Ride',
  swim: 'Swim',
  weightTraining: 'WeightTraining',
  yoga: 'Yoga',
  walk: 'Walk',
  hike: 'Hike',
  other: 'Other',
};

const CANONICAL_ORDER = Object.values(ICON_TO_CANONICAL);
const CANONICAL_INDEX: Record<string, number> = CANONICAL_ORDER.reduce(
  (acc, sport, index) => {
    acc[sport] = index;
    return acc;
  },
  {} as Record<string, number>
);

const normalizeSportType = (sportType?: string | null): string => {
  const icon = getSportIconMeta(sportType ?? undefined).icon;
  return ICON_TO_CANONICAL[icon] ?? 'Other';
};

export const sortWeeklySummary = (
  a: WeeklySummaryType,
  b: WeeklySummaryType
): number => {
  const aIndex = CANONICAL_INDEX[a.sportType] ?? CANONICAL_ORDER.length;
  const bIndex = CANONICAL_INDEX[b.sportType] ?? CANONICAL_ORDER.length;

  return aIndex - bIndex;
};

export const buildWeeklySummaryFromActivitiesAndWorkouts = (
  activities?: ActivityWithRelations[],
  workouts?: WorkoutWithRelations[]
): WeeklySummaryType[] => {
  const summaryMap = new Map<string, WeeklySummaryType>();

  const getOrCreateEntry = (sportType?: string) => {
    const key = sportType || '';
    if (!summaryMap.has(key)) {
      summaryMap.set(key, {
        sportType: key,
        doneDistance: 0,
        doneTime: 0,
        plannedDistance: 0,
        plannedTime: 0,
      });
    }
    return summaryMap.get(key)!;
  };

  (activities ?? []).forEach((activity) => {
    const target = getOrCreateEntry(
      normalizeSportType(activity.sportType ?? undefined)
    );
    target.doneDistance += activity.distance ?? 0;
    target.doneTime += activity.movingTime ?? 0;
  });

  (workouts ?? []).forEach((workout) => {
    const target = getOrCreateEntry(
      normalizeSportType(workout.sportType ?? undefined)
    );
    target.plannedDistance += workout.distance ?? 0;
    target.plannedTime += workout.duration ?? 0;
  });

  return Array.from(summaryMap.values()).sort(sortWeeklySummary);
};
